import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { exportType, type MatchingOperator } from '../../../../../../../../modules/audiences/AudienceForm/constants';
import type { AudienceForm, GroupLike } from '../../../../../../../../modules/audiences/AudienceForm/types';
import { AudienceFields } from '../../../../../../../../modules/audiences/AudienceForm/types';
import {
  hasInternationalCriteriaInDefinition,
  hasInternationalCriteriaInGroups,
} from '../../../../../utils/hasInternationalCriteria';
import {
  getShouldUseRegionFirstFlow,
  getSiblingOperatorRestrictionReason,
  type MarketOperatorRestrictionReason,
} from '../../../../../utils/marketOperatorRestrictions';
import { buildGroupsPath } from '../../../utils/getGroupFromPath';

export type GroupListCriteriaFieldName = 'normalized_definition.includes' | 'normalized_definition.excludes';

export type UseGroupListMarketOperatorsParams<TField extends GroupListCriteriaFieldName> = {
  fieldName: TField;
  isCreate?: boolean;
  isCreateFromTemplate?: boolean;
};

export type UseGroupListMarketOperatorsResult = {
  groupsPath: ReturnType<typeof buildGroupsPath>;
  shouldUseRegionFirstFlow: boolean;
  topLevelMarketOperatorReason: MarketOperatorRestrictionReason | null;
  subgroupRestrictionReasonByParentIndex: Record<number, MarketOperatorRestrictionReason | null>;
  observableGroupOperator: MatchingOperator;
  isIdentityGraphMatchType: boolean;
};

export const useGroupListMarketOperators = <TField extends GroupListCriteriaFieldName>({
  fieldName,
  isCreate,
  isCreateFromTemplate,
}: UseGroupListMarketOperatorsParams<TField>): UseGroupListMarketOperatorsResult => {
  const { control } = useFormContext<AudienceForm>();

  const observableNormalizedDefinition = useWatch({
    name: AudienceFields.NormalizedDefinition,
    control,
  });

  const groupsPath = buildGroupsPath(fieldName);

  const observableAccountId = useWatch({ name: AudienceFields.OrganizationId, control }) ?? '';

  const observableGroups = useWatch({
    name: `${fieldName}.groups`,
    control,
  }) as GroupLike[] | undefined;

  const hasInternationalCriteria = useMemo(
    () =>
      hasInternationalCriteriaInDefinition(observableNormalizedDefinition) ||
      hasInternationalCriteriaInGroups(observableGroups),
    [observableNormalizedDefinition, observableGroups]
  );

  const shouldUseRegionFirstFlow = getShouldUseRegionFirstFlow({
    isCreate,
    isCreateFromTemplate,
    hasInternationalCriteria,
  });

  const topLevelMarketOperatorReason = useMemo(() => {
    if (!shouldUseRegionFirstFlow) {
      return null;
    }

    return getSiblingOperatorRestrictionReason(observableGroups ?? [], observableAccountId, true);
  }, [shouldUseRegionFirstFlow, observableGroups, observableAccountId]);

  const subgroupRestrictionReasonByParentIndex = useMemo(() => {
    if (!shouldUseRegionFirstFlow) {
      return {};
    }

    const out: Record<number, MarketOperatorRestrictionReason | null> = {};

    (observableGroups ?? []).forEach((node, index) => {
      if ('groups' in node && node.groups?.length) {
        out[index] = getSiblingOperatorRestrictionReason(node.groups, observableAccountId, true);
      }
    });

    return out;
  }, [shouldUseRegionFirstFlow, observableGroups, observableAccountId]);

  const observableGroupOperator = useWatch({
    name: `${fieldName}.operator`,
    control,
  }) as MatchingOperator;

  const observableMatchType = useWatch({
    name: AudienceFields.ExportType,
    control,
  });

  const isIdentityGraphMatchType = observableMatchType === exportType.OA_MATCH;

  return {
    groupsPath,
    shouldUseRegionFirstFlow,
    topLevelMarketOperatorReason,
    subgroupRestrictionReasonByParentIndex,
    observableGroupOperator,
    isIdentityGraphMatchType,
  };
};
